/**
 * lib/telegram.ts — the one and only contact surface with `window.Telegram.WebApp`.
 *
 * Nothing else in `apps/web` may touch that global, and `apps/web/test/telegram.test.ts`
 * proves nothing does. Two properties make the rule worth enforcing.
 *
 * ## 1. The app has to work with no Telegram at all
 *
 * Opening the built app in a plain browser must produce a working, developable interface —
 * that is the whole «режим разработки» of the checklist. So {@link resolveWebApp} falls back
 * to {@link createDevWebApp}, a cooperative in-memory stand-in that reads
 * `#tgWebAppVersion`, `#tgWebAppPlatform` and `#tgWebAppData` out of `location.hash`.
 *
 * **Detection is by feature** (`typeof wa.ready === 'function'`), never by user agent. A
 * user-agent test is a test of a string anybody can set, and it is wrong the first time
 * Telegram ships a new client.
 *
 * ## 2. Every capability is version-gated, and the comparison is not a number
 *
 * The Mini Apps API is one object whose members appear over time. An older client either
 * throws or — worse — silently does nothing. So every recent method sits behind
 * {@link TelegramFacade.isVersionAtLeast} **and** a `typeof` check, and each gated call
 * reports what happened by returning a boolean instead of throwing.
 *
 * **Gating is never a float comparison of the version string.** `parseFloat('7.10')` is
 * `7.1`, which is *less* than `7.7` — so a capability added in 7.7 would be silently
 * switched off on every client that has it. {@link compareVersions} compares dotted integer
 * segments instead.
 *
 * ## The deck answers the hand (task 50)
 *
 * {@link TelegramFacade.haptic} — Telegram's own vibration (`HapticFeedback`, 6.1): a light knock
 * for START·STOP, 33, 45 and «играть»/«пауза», a tick for the tempo clicking into zero, and the
 * error buzz when the player's line of trouble appears. Behind the version and `typeof`, and every
 * call in `try`: a phone or a client that cannot vibrate is the same app without it, and the
 * browser of the development mode is silent.
 *
 * ## What is deliberately absent
 *
 * - **The client's theme palette.** The app is one art-directed object in the colours of the
 *   flag and ignores the client's colours for every surface, text and accent. The only thing
 *   taken from Telegram is colour *assignment* — the ground is written into Telegram's own
 *   header and bottom bar so the seam does not show.
 * - **The Telegram-styled main button.** It would cover the mini player, and every action
 *   this product has is in the page.
 * - **«Send data and close».** It closes the Mini App, which kills playback instantly.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * The shape we use
 * ──────────────────────────────────────────────────────────────────────────── */

/** Exactly the members this project calls. Not a copy of Telegram's whole surface. */
export interface RawWebApp {
  readonly version?: string;
  readonly platform?: string;
  readonly initData?: string;
  readonly viewportStableHeight?: number;
  ready?: () => void;
  expand?: () => void;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  enableClosingConfirmation?: () => void;
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  onEvent?: (event: string, handler: (data?: unknown) => void) => void;
  offEvent?: (event: string, handler: (data?: unknown) => void) => void;
  shareMessage?: (msgId: string, callback?: (sent: boolean) => void) => void;
  /** Task 54: 8.0. */
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  readonly isFullscreen?: boolean;
  /** Task 53: 8.0, in points. */
  readonly safeAreaInset?: TelegramInsets;
  readonly contentSafeAreaInset?: TelegramInsets;
  /** Task 50: 6.1. */
  readonly HapticFeedback?: {
    impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged?: () => void;
  };
}

/** Task 53: a margin on each side of the screen, in points (Bot API 8.0). */
export interface TelegramInsets {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

/** Task 53: the events of the screen (8.0) the probe of the pult writes down. */
export const SCREEN_EVENTS = Object.freeze([
  'fullscreenChanged',
  'fullscreenFailed',
  'safeAreaChanged',
  'contentSafeAreaChanged',
] as const);

/**
 * Task 50: what the hand is told. `impact` — a key of the deck, «играть»/«пауза»; `selection` —
 * the tempo clicking into zero; `error` — the player's line of trouble appearing.
 */
export type HapticKind = 'impact' | 'selection' | 'error';

/**
 * What became of a share dialog (task 15): the message went, the person closed the dialog, or
 * the client could not open one at all. Only the last is a failure — `spec.md` «Бот»: «Отказ
 * пользователя ошибкой не считается».
 */
export type ShareOutcome = 'sent' | 'declined' | 'unavailable';

interface TelegramGlobal {
  readonly Telegram?: { readonly WebApp?: RawWebApp };
}

/** What the rest of the app is given. */
export interface TelegramFacade {
  /** `true` when a real Telegram client answered; `false` in the browser stand-in. */
  readonly isReal: boolean;
  readonly version: string;
  readonly platform: string;
  /** The launch string, verbatim, for `POST /api/auth/telegram`. Never parsed here. */
  readonly initData: string;
  isVersionAtLeast(version: string): boolean;
  /** Tells Telegram the page is painted, expands it and turns off the closing swipe. */
  ready(): void;
  /** Opens a `t.me` link inside Telegram. Returns whether the client could. */
  openTelegramLink(url: string): boolean;
  /**
   * Task 13. Opens an outside address through Telegram (6.1) — «Открыть в YouTube»: the client
   * hands it to the YouTube app or the browser, and the Mini App stays where it was. Returns
   * whether the client could; a caller that gets `false` lets its own link be followed.
   */
  openLink(url: string): boolean;
  /** Paints Telegram's own chrome in our ground colour. Returns whether it could. */
  paintChrome(color: string): boolean;
  /** The height the app should occupy, or `undefined` when the client did not say. */
  stableHeight(): number | undefined;
  /**
   * Calls `handler` with the new stable height every time the client reports one, and
   * returns the unsubscribe. Does nothing on a client that cannot say.
   *
   * The height at launch is not the height for good: `ready()` asks the client to expand,
   * the half-open sheet becomes the full window a moment later, and the frame drawn to the
   * first number would leave the tabs floating mid-screen or cut off under the edge.
   */
  onStableHeightChange(handler: (height: number) => void): () => void;
  /**
   * Task 07. Asks Telegram to confirm before the Mini App is closed (6.2). Closing stops the
   * music, and a stray swipe must not do that. Returns whether the client could.
   */
  enableClosingConfirmation(): boolean;
  /**
   * Task 07. Calls `handler(false)` when the client reports the app inactive — minimised,
   * another chat on top — and `handler(true)` when it is active again (8.0). Returns the
   * unsubscribe; does nothing on a client that cannot say.
   *
   * This is Telegram's own signal, and deliberately not the page's `visibilitychange`: on
   * Android minimising fires that one too, and a handler on it is where a «pause when hidden»
   * creeps in (`fdm/no-visibility-pause`).
   */
  onActiveChange(handler: (active: boolean) => void): () => void;
  /**
   * Task 09. Turns Telegram's vertical swipes (minimise or close with a swipe) off until the
   * returned release is called, and back on once nothing holds them (7.7). The release may be
   * called any number of times; only the first counts.
   *
   * `spec.md` «Пластинка»: a finger on the record must not minimise the app, and the swipes
   * come back on every way out of the touch. They come back **only if nothing else holds
   * them**, and {@link TelegramFacade.ready} holds them for the whole life of the app (task
   * 04) — so inside Telegram today a released record leaves them off, as they were before
   * the touch. The count is what keeps the two decisions from undoing each other.
   */
  holdVerticalSwipes(): () => void;
  /**
   * Task 50. One vibration of Telegram's own (6.1). Returns whether one was asked for; never
   * throws — see «The deck answers the hand».
   */
  haptic(kind: HapticKind): boolean;
  /** Task 15. Whether the client can open the share dialog (8.0) — whether to draw the button. */
  canShareMessage(): boolean;
  /**
   * Task 15. Opens Telegram's own dialog «кому отправить» for a message the bot prepared
   * (`POST /api/tracks/:id/share`), and says how it ended. Never rejects. A dialog that never
   * answers leaves the promise pending, so a caller must not wait on it to redraw.
   */
  shareMessage(id: string): Promise<ShareOutcome>;
  /**
   * Task 54. Asks Telegram for the whole screen (8.0) — the pult on its side is shown by Telegram
   * only so (`.omc/wiki/pult-razmer-na-iphone.md`). Returns whether it could ask; the answer
   * comes later as `fullscreenChanged` or `fullscreenFailed`, and a refusal leaves the window as
   * it was. Never throws.
   */
  requestFullscreen(): boolean;
  /** Task 54. Whether the client can be asked for the whole screen at all (8.0). */
  canRequestFullscreen(): boolean;
  /** Task 54. Gives the whole screen back (8.0). Returns whether it could ask. Never throws. */
  exitFullscreen(): boolean;
  /** Task 53. Whether the whole screen is on, as the client says (8.0); `undefined` where it cannot. */
  isFullscreen(): boolean | undefined;
  /**
   * Task 53. The system's safe area (`safe`) and Telegram's own buttons on top of it (`content`),
   * as the client says (8.0); each `undefined` where it does not.
   */
  safeAreas(): { safe: TelegramInsets | undefined; content: TelegramInsets | undefined };
  /**
   * Task 53. Calls `handler` with the name of each screen event (8.0) and what came with it —
   * a refusal's reason, for one. Returns the stop; does nothing on a client that cannot say.
   */
  onScreenEvent(handler: (event: string, detail: string) => void): () => void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Versions
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `-1`, `0` or `1`, comparing dotted integers.
 *
 * `'7.10'` vs `'7.7'` → `1`. A float comparison would say `-1`, which is the bug this
 * function exists to make impossible.
 */
export function compareVersions(a: string, b: string): number {
  const left = a.split('.');
  const right = b.split('.');
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const x = Number.parseInt(left[i] ?? '0', 10);
    const y = Number.parseInt(right[i] ?? '0', 10);
    const xs = Number.isNaN(x) ? 0 : x;
    const ys = Number.isNaN(y) ? 0 : y;
    if (xs !== ys) return xs < ys ? -1 : 1;
  }
  return 0;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The development stand-in
 * ──────────────────────────────────────────────────────────────────────────── */

/** The fragment keys a developer (or a test) can set to shape the stand-in. */
export const DEV_HASH_KEYS = Object.freeze({
  version: 'tgWebAppVersion',
  platform: 'tgWebAppPlatform',
  data: 'tgWebAppData',
});

/**
 * A cooperative stand-in that answers everything and does nothing.
 *
 * Deliberately not a throwing mock: the point is that the built app RUNS in a plain
 * browser. Every method is a no-op that succeeds, so the interface renders and the only
 * thing that does not happen is a conversation with Telegram.
 */
export function createDevWebApp(hash: string): RawWebApp {
  const params = new URLSearchParams(hash.replace(/^#/u, ''));
  return {
    version: params.get(DEV_HASH_KEYS.version) ?? '9.0',
    platform: params.get(DEV_HASH_KEYS.platform) ?? 'web',
    initData: params.get(DEV_HASH_KEYS.data) ?? '',
    ready: () => undefined,
    expand: () => undefined,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Resolution
 * ──────────────────────────────────────────────────────────────────────────── */

/** The real client if there is one, otherwise the stand-in. */
export function resolveWebApp(
  global: TelegramGlobal = globalThis as unknown as TelegramGlobal,
  hash = typeof location === 'undefined' ? '' : location.hash,
): { raw: RawWebApp; isReal: boolean } {
  const candidate = global.Telegram?.WebApp;
  // By feature, never by user agent: see the header.
  if (candidate !== undefined && typeof candidate.ready === 'function') {
    return { raw: candidate, isReal: true };
  }
  return { raw: createDevWebApp(hash), isReal: false };
}

/** Insets as the client gave them, or `undefined` where they are not four numbers. */
function insetsOf(value: TelegramInsets | undefined): TelegramInsets | undefined {
  if (value === undefined) return undefined;
  const { top, bottom, left, right } = value;
  return [top, bottom, left, right].every((n) => typeof n === 'number' && Number.isFinite(n))
    ? { top, bottom, left, right }
    : undefined;
}

/** What came with an event, in one line: `{ error: 'UNSUPPORTED' }` → `error=UNSUPPORTED`. */
function detailOf(data: unknown): string {
  if (typeof data !== 'object' || data === null) return '';
  return Object.entries(data)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(' ');
}

/** Wraps a raw object in the facade the app uses. */
export function createTelegram(resolved = resolveWebApp()): TelegramFacade {
  const { raw, isReal } = resolved;
  const version = raw.version ?? '6.0';

  const isVersionAtLeast = (wanted: string): boolean => compareVersions(version, wanted) >= 0;

  /** How many holders want the vertical swipes off. They are on exactly at zero. */
  let swipeHolds = 0;
  /** The launch's own hold, taken once and never released. */
  let launchHold: (() => void) | undefined;

  const holdVerticalSwipes = (): (() => void) => {
    if (!isVersionAtLeast('7.7') || typeof raw.disableVerticalSwipes !== 'function') {
      return () => undefined;
    }
    swipeHolds += 1;
    if (swipeHolds === 1) raw.disableVerticalSwipes();
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      swipeHolds -= 1;
      if (swipeHolds === 0) raw.enableVerticalSwipes?.();
    };
  };

  return {
    isReal,
    version,
    platform: raw.platform ?? 'unknown',
    initData: raw.initData ?? '',
    isVersionAtLeast,

    ready(): void {
      raw.ready?.();
      raw.expand?.();
      // 7.7. Without it a downward swipe closes the Mini App — and closing it stops the
      // music, which is the one thing this product must not let happen by accident. A hold
      // that is never released: the record's touch (task 09) must not switch it back on.
      // Called twice under `StrictMode`, and still one hold.
      launchHold ??= holdVerticalSwipes();
    },

    openTelegramLink(url: string): boolean {
      if (typeof raw.openTelegramLink !== 'function') return false;
      raw.openTelegramLink(url);
      return true;
    },

    openLink(url: string): boolean {
      if (!isVersionAtLeast('6.1') || typeof raw.openLink !== 'function') return false;
      try {
        raw.openLink(url);
        return true;
      } catch {
        // The SDK throws on an address it does not take (`WebAppTgUrlInvalid`): the caller's
        // own link is then the way out, not an exception in the middle of a tap.
        return false;
      }
    },

    paintChrome(color: string): boolean {
      let painted = false;
      if (typeof raw.setHeaderColor === 'function') {
        raw.setHeaderColor(color);
        painted = true;
      }
      if (typeof raw.setBackgroundColor === 'function') {
        raw.setBackgroundColor(color);
        painted = true;
      }
      // 7.10 — and `'7.10' < '7.7'` as a float, which is exactly the trap
      // `compareVersions` exists to avoid.
      if (isVersionAtLeast('7.10') && typeof raw.setBottomBarColor === 'function') {
        raw.setBottomBarColor(color);
        painted = true;
      }
      return painted;
    },

    stableHeight(): number | undefined {
      const height = raw.viewportStableHeight;
      return typeof height === 'number' && height > 0 ? height : undefined;
    },

    enableClosingConfirmation(): boolean {
      if (!isVersionAtLeast('6.2') || typeof raw.enableClosingConfirmation !== 'function') {
        return false;
      }
      raw.enableClosingConfirmation();
      return true;
    },

    onActiveChange(handler: (active: boolean) => void): () => void {
      if (!isVersionAtLeast('8.0') || typeof raw.onEvent !== 'function') return () => undefined;
      const onActivated = (): void => {
        handler(true);
      };
      const onDeactivated = (): void => {
        handler(false);
      };
      raw.onEvent('activated', onActivated);
      raw.onEvent('deactivated', onDeactivated);
      return () => {
        raw.offEvent?.('activated', onActivated);
        raw.offEvent?.('deactivated', onDeactivated);
      };
    },

    holdVerticalSwipes,

    haptic(kind: HapticKind): boolean {
      const feedback = raw.HapticFeedback;
      if (!isVersionAtLeast('6.1') || feedback === undefined) return false;
      try {
        if (kind === 'impact' && typeof feedback.impactOccurred === 'function') {
          feedback.impactOccurred('light');
          return true;
        }
        if (kind === 'selection' && typeof feedback.selectionChanged === 'function') {
          feedback.selectionChanged();
          return true;
        }
        if (kind === 'error' && typeof feedback.notificationOccurred === 'function') {
          feedback.notificationOccurred('error');
          return true;
        }
      } catch {
        // A client that refuses: the tap has already done what it does, without the knock.
      }
      return false;
    },

    canShareMessage(): boolean {
      return isVersionAtLeast('8.0') && typeof raw.shareMessage === 'function';
    },

    shareMessage(id: string): Promise<ShareOutcome> {
      if (!isVersionAtLeast('8.0') || typeof raw.shareMessage !== 'function') {
        return Promise.resolve('unavailable');
      }
      return new Promise<ShareOutcome>((resolve) => {
        try {
          // Called on the object, like every other method here: the SDK's own may read `this`.
          raw.shareMessage?.(id, (sent) => {
            resolve(sent ? 'sent' : 'declined');
          });
        } catch {
          // The SDK throws while another share dialog is open (`WebAppShareMessageOpened`), and
          // on a client it thinks too old: nothing opened, which the button says in one line.
          resolve('unavailable');
        }
      });
    },

    requestFullscreen(): boolean {
      if (!isVersionAtLeast('8.0') || typeof raw.requestFullscreen !== 'function') return false;
      try {
        raw.requestFullscreen();
        return true;
      } catch {
        // A client that refuses outright: the pult stands upright in the window it has.
        return false;
      }
    },

    canRequestFullscreen(): boolean {
      return isVersionAtLeast('8.0') && typeof raw.requestFullscreen === 'function';
    },

    exitFullscreen(): boolean {
      if (!isVersionAtLeast('8.0') || typeof raw.exitFullscreen !== 'function') return false;
      try {
        raw.exitFullscreen();
        return true;
      } catch {
        return false;
      }
    },

    isFullscreen(): boolean | undefined {
      if (!isVersionAtLeast('8.0')) return undefined;
      return typeof raw.isFullscreen === 'boolean' ? raw.isFullscreen : undefined;
    },

    safeAreas() {
      if (!isVersionAtLeast('8.0')) return { safe: undefined, content: undefined };
      return { safe: insetsOf(raw.safeAreaInset), content: insetsOf(raw.contentSafeAreaInset) };
    },

    onScreenEvent(handler: (event: string, detail: string) => void): () => void {
      const on = raw.onEvent;
      if (!isVersionAtLeast('8.0') || typeof on !== 'function') return () => undefined;
      const listeners = SCREEN_EVENTS.map((event) => {
        const listener = (data?: unknown): void => {
          handler(event, detailOf(data));
        };
        raw.onEvent?.(event, listener);
        return [event, listener] as const;
      });
      return () => {
        for (const [event, listener] of listeners) raw.offEvent?.(event, listener);
      };
    },

    onStableHeightChange(handler: (height: number) => void): () => void {
      if (typeof raw.onEvent !== 'function') return () => undefined;
      // `viewportChanged` fires during a drag too; `viewportStableHeight` only moves once the
      // client has settled, so re-reading it and ignoring an unchanged value is the filter.
      let last = raw.viewportStableHeight;
      const listener = (): void => {
        const height = raw.viewportStableHeight;
        if (typeof height !== 'number' || height <= 0 || height === last) return;
        last = height;
        handler(height);
      };
      raw.onEvent('viewportChanged', listener);
      return () => {
        raw.offEvent?.('viewportChanged', listener);
      };
    },
  };
}

/** The instance the app uses. Built once, at import. */
export const telegram: TelegramFacade = createTelegram();
